import { useCallback, useEffect } from 'react';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';

import { Field, useFormikContext, ErrorMessage } from 'formik';
import { Select } from 'formik-material-ui';

import { BuildingsResponse, SectionResponse } from '../../../services/models';
import { ValuesModel } from '..';
import { PlaceService } from '../../../services';
import useStepper from '../../../hooks/useStepper';
import useLocalStorage from '../../../hooks/useLocalStorage';
import { makeFloor, formatDate, formatError, snackbarUtils } from '../../../utils';

import StyledInput from './StyledInput';
import useStyles from './styles';

const compare = (a: number, b: number) => {
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
}

const WorkLocation = () => {
  const { setPlaceData } = useStepper();
  const { values, setFieldValue, resetForm } = useFormikContext<ValuesModel>();
  const classes = useStyles();

  const [buildings, setBuildings] = useLocalStorage<BuildingsResponse[]>('buildings', []);
  const [floors, setFloors] = useLocalStorage<number[]>('floors', []);
  const [sections, setSections] = useLocalStorage<SectionResponse[]>('sections', []);
  const [desks, setDesks] = useLocalStorage<string[]>('desks', []);

  const formattedDate = formatDate(values.date.toString());

  // useEffect(() => console.log(values), [values]);

  useEffect(() => {
    const fetchBuildings = async () => {
      try {
        const { data } = await PlaceService.availableBuildings(formattedDate);
        setBuildings(data);
      } catch (error) {
        console.error('fetchBuildings: ', error);
        snackbarUtils.error(formatError(error));
      }
    };

    fetchBuildings();
  }, [formattedDate, setBuildings]);

  const fetchFloor = useCallback(async (building_id: string) => {
    try {
      const { data } = await PlaceService.availableFloor(formattedDate, Number(building_id));
      setFloors(data.sort(compare));
    } catch (error) {
      console.error('fetchFloor: ', error);
      snackbarUtils.error(formatError(error));
    }
  }, [formattedDate, setFloors]);

  const handleSelectBuilding = useCallback((event: any) => {
    if (!!values.floor) resetForm({
      values: {
        ...values,
        floor: '',
        section: '',
        desk: '',
      }
    });
    fetchFloor(event.target.value);
    setFieldValue('building', event.target.value);
    const building = buildings?.find(building => Number(event.target.value) === building.id);
    if (!!building) setPlaceData(placeValues => ({ ...placeValues, buildingName: building.title }));
  }, [values, resetForm, fetchFloor, setFieldValue, buildings, setPlaceData]);

  const fetchSection = useCallback(async (building_id: string, floor: string) => {
    try {
      const { data } = await PlaceService.availableSection(formattedDate, Number(building_id), floor);
      setSections(data);
    } catch (error) {
      console.error('fetchSection: ', error);
      snackbarUtils.error(formatError(error));
    }
  }, [formattedDate, setSections])

  const fetchDesks = async (building_id: string, floor: string, section_id: number) => {
    try {
      const { data } = await PlaceService.availableDesk(formattedDate, Number(building_id), floor, section_id);
      setDesks(data.map(p => p.desk));
    } catch (error) {
      console.error('fetchSection: ', error);
      snackbarUtils.error(formatError(error));
    }
  }

  const clearFromSection = useCallback(() => {
    if (!!values.section)
      resetForm({
        values: {
          ...values,
          section: '',
          desk: '',
        }
      }); else return;
  }, [resetForm, values]);

  const clearDesk = useCallback(() => {
    if (!!values.section)
      resetForm({
        values: {
          ...values,
          desk: '',
        }
      }); else return;
  }, [resetForm, values]);

  return (
    <Container className={classes.container}>
      <Typography variant="h3">Onde quer trabalhar?</Typography>
      <Typography>Escolha em qual mesa quer se acomodar para trabalhar?</Typography>

      <Grid container item xs={12} spacing={3} className={classes.formContainer}>
        <Grid container item xs={12} spacing={3}>
          <Grid item xs={12} sm={6}>
            <FormControl data-cy="building-input" className={classes.formControl} fullWidth>
              <InputLabel htmlFor="select-building">Prédio</InputLabel>
              <Field
                name="building"
                component={Select}
                variant="outlined"
                input={<StyledInput />}
                inputProps={{
                  id: 'select-building',
                  name: "building",
                }}
                onChange={handleSelectBuilding}
                displayEmpty
              >
                <MenuItem value="" disabled><em>Prédio</em></MenuItem>
                {buildings?.map(building => (
                  <MenuItem key={building.id} value={building.id.toString()}>
                    {building.title}
                  </MenuItem>
                ))}
              </Field>
              <FormHelperText><ErrorMessage name="building" /></FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl data-cy="floor-input" className={classes.formControl} fullWidth>
              <InputLabel htmlFor="select-floor">Andar</InputLabel>
              <Field
                name="floor"
                component={Select}
                variant="outlined"
                input={<StyledInput />}
                disabled={!values.building}
                inputProps={{
                  id: 'select-floor',
                  name: "floor",
                }}
                onChange={(e: any) => {
                  clearFromSection();
                  setFieldValue('floor', e.target.value);
                  fetchSection(values.building, e.target.value);
                }}
                displayEmpty
              >
                <MenuItem value="" disabled><em>Andar</em></MenuItem>
                {floors?.map((floor) => (
                  <MenuItem key={`floor-${floor}`} value={floor.toString()}>
                    {makeFloor(floor.toString())}
                  </MenuItem>
                ))}
              </Field>
              <FormHelperText><ErrorMessage name="floor" /></FormHelperText>
            </FormControl>
          </Grid>
        </Grid>

        <Grid container item xs={12} spacing={3}>
          <Grid item xs={12} sm={6}>
            <FormControl
              data-cy="section-input"
              disabled={buildings?.length === 0}
              className={classes.formControl}
              fullWidth
            >
              <InputLabel htmlFor="select-section">Seção</InputLabel>
              <Field
                name="section"
                component={Select}
                variant="outlined"
                input={<StyledInput />}
                disabled={values.floor === ""}
                inputProps={{
                  id: 'select-section',
                  name: "section"
                }}
                onChange={(e: any) => {
                  clearDesk();
                  setFieldValue('section', e.target.value);
                  const section = sections?.find(section => Number(e.target.value) === section.id);
                  if (!!section) setPlaceData(placeValues => ({ ...placeValues, sectionName: section.title }));
                  fetchDesks(values.building, values.floor, Number(e.target.value));
                }}
                displayEmpty
              >
                <MenuItem value="" disabled><em>Seção</em></MenuItem>
                {sections?.map((section) => (
                  <MenuItem key={`section-${section.id}`} value={section.id.toString()}>
                    {section.title}
                  </MenuItem>
                ))}
              </Field>
              <FormHelperText><ErrorMessage name="section" /></FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl data-cy="desk-input" className={classes.formControl} fullWidth>
              <InputLabel htmlFor="select-desk">Mesa</InputLabel>
              <Field
                name="desk"
                component={Select}
                variant="outlined"
                input={<StyledInput />}
                disabled={values.section === ""}
                inputProps={{
                  id: 'select-desk',
                  name: "desk"
                }}
                displayEmpty
              >
                <MenuItem value="" disabled><em>Mesa</em></MenuItem>
                {desks?.map((desk) => (
                  <MenuItem key={`desk-${desk}`} value={desk.toString()}>
                    {desk}
                  </MenuItem>
                ))}
              </Field>
              <FormHelperText><ErrorMessage name="desk" /></FormHelperText>
            </FormControl>
          </Grid>
        </Grid>
      </Grid>
    </Container>
  );
}

export default WorkLocation;